<template>
  <div class="area">
    <div v-if="!loading && !data.length" class="center">
      <el-icon>
        <DataAnalysisElementIcon />
      </el-icon>
      There is no activity at the moment.
    </div>
    <div
      v-else
      ref="chartdiv"
      v-loading="loading"
      class="main-chart"
      element-loading-text="Activity loading"
      element-loading-background="transparent"
    />
  </div>
</template>

<script lang="ts">
import {
  ref,
  onMounted,
  onBeforeUnmount,
  defineComponent,
  watch,
  shallowRef,
  nextTick
} from 'vue'
import createAxisAndSeries from '@/utils/charts'

import { am4core, am4charts } from '@/utils/async_chart_loader'

export default defineComponent({
  name: 'MainChart',
  props: {
    data: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  setup(props, context) {
    const chartdiv = ref(null)
    const chartBox = shallowRef()
    let renderVersion = 0

    const renderChart = async (data: any[]) => {
      const currentRender = ++renderVersion
      clearChart()
      if (data.length < 1) {
        return
      }

      await nextTick()
      if (currentRender !== renderVersion || !chartdiv.value) {
        return
      }

      const chart = am4core.create(chartdiv.value, am4charts.XYChart)
      chart.responsive.enabled = true
      // Enable chart cursor
      chart.cursor = new am4charts.XYCursor()
      chart.cursor.lineX.disabled = true
      chart.cursor.lineY.disabled = true
      chart.scrollbarX = new am4core.Scrollbar()
      // Add data
      chart.data = data
      chart.colors.list = [
        am4core.color('#7d7d7d'),
        am4core.color('#1890FF'),
        am4core.color('#ff0000')
      ]
      // Create axes
      let dateAxis = chart.xAxes.push(new am4charts.DateAxis())
      dateAxis.renderer.minGridDistance = 50

      createAxisAndSeries(
        chart,
        'conversions',
        'date',
        'Conversions',
        false,
        'circle',
        false
      )
      createAxisAndSeries(
        chart,
        'spends',
        'date',
        'Spends',
        false,
        'circle',
        false
      )
      createAxisAndSeries(
        chart,
        'impressions',
        'date',
        'Impressions',
        false,
        'circle',
        false
      )

      chartBox.value = chart
    }

    const clearChart = () => {
      if (chartBox.value && !chartBox.value.isDisposed) {
        chartBox.value.dispose()
        chartBox.value = null
      }
    }

    onMounted(() => {
      renderChart(props.data)
    })

    onBeforeUnmount(() => {
      clearChart()
    })

    watch(
      (_: any) => props.data,
      (_: any) => renderChart(props.data)
    )

    const activeTab = ref()

    return { chartdiv, activeTab }
  }
})
</script>

<style lang="scss" scoped>
.area {
  height: 400px !important;
  position: relative;

  .main-chart {
    background: #fff;
    height: 100%;
  }

  .el-radio-group {
    margin: 10px 0;
  }

  .center {
    position: absolute;
    margin: 0;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }
}

:deep(.el-card.is-always-shadow) {
  box-shadow: none;
}

:deep(svg) {
  font-size: 0.8em;
}
</style>
